import { useEffect, useState, Component } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { RefreshCw, Copy, AlertCircle, Server, Monitor, Activity, CheckCircle2, XCircle, Zap, Heart, Download, X, Search } from "lucide-react";

// Coerce any value safely into a renderable string. Catches the case where
// `ev.message` (or any text-y field) accidentally arrives as an object — this
// crashed AdminLogs hard with "Objects are not valid as a React child" when a
// legacy client_events doc had `message` stored as `{}`. Returns a stable
// JSON-stringified representation for objects and "—" for null/undefined.
const txt = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
};
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { getRing, getRouteHistory, snapshotSession, getAppMeta, getConsoleMirror } from "../lib/clientLog";

const TS = (s) => {
  try { return new Date(s).toLocaleString(); } catch { return s || ""; }
};

function StatusPill({ status }) {
  if (status === null || status === undefined) {
    return <span className="text-[10px] font-mono bg-slate-200 text-slate-700 rounded px-1.5 py-0.5">—</span>;
  }
  // Legacy docs may have `status` as a non-number. Coerce defensively so one
  // bad doc can't crash the whole AdminLogs renderer.
  const n = typeof status === "number" ? status : Number(status);
  const label = typeof status === "string" || typeof status === "number" ? String(status) : JSON.stringify(status);
  const ok = !isNaN(n) && n >= 200 && n < 300;
  const warn = !isNaN(n) && n >= 400 && n < 500;
  const cls = ok
    ? "bg-emerald-100 text-emerald-800"
    : warn
      ? "bg-amber-100 text-amber-800"
      : "bg-red-100 text-red-800";
  return <span className={`text-[10px] font-mono rounded px-1.5 py-0.5 ${cls}`}>{label}</span>;
}

// Per-row error boundary — if ONE `ev` has a shape we can't render, show a
// fallback inline row instead of killing the whole AdminLogs page. Stores the
// caught error's message + the offending object's keys so we can surface them
// in the UI for post-mortem. Still ships to /api/client-log via the outer
// ErrorBoundary so crashes are tracked.
class RowBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (this.state.err) {
      const { ev, index } = this.props;
      const keys = ev && typeof ev === "object" ? Object.keys(ev).join(", ") : "(not an object)";
      return (
        <div className="p-3 bg-rose-50 border-l-4 border-rose-400 text-[11px] font-mono text-rose-900" data-testid={`client-event-${index}-bad`}>
          <div className="font-semibold">Row #{index} failed to render</div>
          <div className="text-rose-700 break-words">{String(this.state.err?.message || this.state.err)}</div>
          <div className="mt-1 text-slate-600">keys on offending row: [{keys}]</div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AdminLogs() {
  const { user, loading: authLoading } = useAuth();
  const [clientEvents, setClientEvents] = useState([]);
  const [backendLogs, setBackendLogs] = useState({});
  const [backendVersion, setBackendVersion] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [fbLatency, setFbLatency] = useState(null);
  const [deployCheck, setDeployCheck] = useState(null);
  const [healthHistory, setHealthHistory] = useState(null);
  const [healthWindow, setHealthWindow] = useState(24);
  const [limit, setLimit] = useState(200);
  const [busy, setBusy] = useState(false);
  const [lastLoaded, setLastLoaded] = useState(null);
  const [copyFallback, setCopyFallback] = useState("");
  const [acctDebug, setAcctDebug] = useState(null);
  const [acctDebugBusy, setAcctDebugBusy] = useState(false);

  const runDiagnostics = async () => {
    setDiagBusy(true);
    try {
      const { data } = await api.get("/admin/diagnostics");
      setDiagnostics(data);
      toast[data.ok ? "success" : "error"](data.ok ? "Diagnostics: all green" : "Diagnostics: one or more checks failed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnostics failed");
    } finally {
      setDiagBusy(false);
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const [c, b, v, f, h] = await Promise.all([
        api.get("/admin/logs/client", { params: { limit } }),
        api.get("/admin/logs/backend", { params: { lines: limit } }),
        api.get("/admin/version").catch(() => ({ data: null })),
        api.get("/admin/fb-latency", { params: { limit: 100 } }).catch(() => ({ data: null })),
        api.get("/admin/health-history", { params: { hours: healthWindow } }).catch(() => ({ data: null })),
      ]);
      setClientEvents(c.data?.events || []);
      setBackendLogs(b.data?.files || {});
      setBackendVersion(v.data || null);
      setFbLatency(f.data || null);
      setHealthHistory(h.data || null);
      setLastLoaded(new Date());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load logs");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user?.role === "admin") load();
    // eslint-disable-next-line
  }, [authLoading, user?.role, limit, healthWindow]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="p-8 max-w-xl" data-testid="admin-logs-forbidden">
        <div className="border border-red-200 bg-red-50 rounded-md p-4 text-sm text-red-800">
          <b>Admin only.</b> /admin/logs is restricted to workspace admins.
        </div>
      </div>
    );
  }

  const copyForE1 = async () => {
    const bundle = {
      generated_at: new Date().toISOString(),
      preview_url: window.location.origin,
      workspace_id: user.workspace_id || null,
      app_meta: getAppMeta(),
      session_snapshot: snapshotSession(),
      route_history: getRouteHistory(),
      console_mirror: getConsoleMirror(),
      ring_buffer_tab_requests: getRing(),
      diagnostics,
      fb_latency: fbLatency,
      client_events: clientEvents,
      backend_logs: backendLogs,
    };
    const text = "```json\n" + JSON.stringify(bundle, null, 2) + "\n```";
    try {
      await navigator.clipboard.writeText(text);
      setCopyFallback("");
      toast.success(`Copied ${clientEvents.length} client events + backend log tails + diagnostics to clipboard`);
    } catch {
      setCopyFallback(text);
      toast.error("Clipboard blocked — select the textarea below and copy manually");
    }
  };

  // Sidesteps the clipboard size limit (which can silently truncate above
  // ~1 MB on some browsers — exactly what bites us at limit=500/1000). Writes
  // the same bundle to a downloadable .json file the user can attach in chat.
  const downloadBundle = () => {
    const bundle = {
      generated_at: new Date().toISOString(),
      preview_url: window.location.origin,
      workspace_id: user.workspace_id || null,
      app_meta: getAppMeta(),
      session_snapshot: snapshotSession(),
      route_history: getRouteHistory(),
      console_mirror: getConsoleMirror(),
      ring_buffer_tab_requests: getRing(),
      diagnostics,
      fb_latency: fbLatency,
      client_events: clientEvents,
      backend_logs: backendLogs,
    };
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.href = url;
    a.download = `rsoc_logs_${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    const sizeKB = Math.round(blob.size / 1024);
    toast.success(`Downloaded · ${clientEvents.length} client events · ${sizeKB} KB`);
  };

  const debugAdAccount = async (ad_account_id) => {
    setAcctDebugBusy(true);
    setAcctDebug({ ad_account_id, loading: true });
    try {
      const { data } = await api.get("/admin/fb-account-debug", { params: { ad_account_id } });
      setAcctDebug(data);
    } catch (e) {
      setAcctDebug({
        ad_account_id,
        ok: false,
        error: e.response?.data?.detail || e.message,
      });
      toast.error(e.response?.data?.detail || "Account debug failed");
    } finally {
      setAcctDebugBusy(false);
    }
  };

  const shareLogUrl = async () => {
    try {
      const { data } = await api.post("/admin/logs/share-token");
      // Always prefer the browser's current origin — guaranteed to be the
      // external URL the admin is already on, regardless of internal ingress
      // rewrites. Backend's `share_url` is a fallback for curl-only flows.
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const url = origin ? `${origin}${data.path}` : (data.share_url || data.path);
      const text = `${url}\n(valid ${data.expires_in_min} min · max 5 fetches)`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`Share URL copied (valid ${data.expires_in_min} min · send to E1)`);
      } catch {
        setCopyFallback(text);
        toast.error("Clipboard blocked — copy manually from textarea");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mint share URL");
    }
  };

  const runDeployCheck = async () => {
    try {
      const { data } = await api.get("/admin/deploy-check");
      setDeployCheck(data);
      toast[data.all_ok ? "success" : "error"](
        data.all_ok
          ? `Deploy check · ${data.actual_marker} · all ✓`
          : `Deploy check FAILED · expected ${data.expected_marker} got ${data.actual_marker}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deploy check failed");
    }
  };

  const meta = getAppMeta();
  const isStaleBuild = !meta?.build_sha && !meta?.build_env;

  return (
    <div className="p-6 max-w-[1400px]" data-testid="admin-logs-page">
      {isStaleBuild && (
        <div
          data-testid="admin-logs-stale-build-banner"
          className="mb-4 border border-rose-300 bg-rose-50 rounded-md p-4 flex items-start gap-3"
        >
          <div className="bg-rose-600 text-white rounded-full p-1.5 shrink-0">
            <AlertCircle className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-rose-900">
              This build has no version metadata — you may be running outdated code.
            </div>
            <p className="text-xs text-rose-800 mt-0.5 leading-relaxed">
              <b>build_sha</b> and <b>build_env</b> are both <code>null</code>.
              If you recently asked E1 to fix something and still see the old behavior,
              you likely need to <b>Save to GitHub</b> from the Emergent chat and then
              <b> redeploy</b> this production container so the latest code ships.
              Set <code>REACT_APP_GIT_SHA</code> + <code>REACT_APP_ENV</code> on your
              frontend build to silence this banner permanently.
            </p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <AlertCircle className="w-5 h-5 text-amber-500" /> Admin Logs
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-mono">
            Client errors (shipped from browsers) + backend supervisor tail.
            {lastLoaded && <span className="ml-2 text-slate-400">· loaded {lastLoaded.toLocaleTimeString()}</span>}
            {backendVersion && (
              <span className="ml-2 text-emerald-600" data-testid="backend-version-chip">
                · backend {backendVersion.marker || "unknown"}{backendVersion.backend_sha ? ` · ${String(backendVersion.backend_sha).slice(0,7)}` : ""} · up {Math.floor((backendVersion.backend_uptime_s || 0) / 60)}m
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            data-testid="admin-logs-limit"
            className="bg-white border border-slate-200 rounded-md px-2 py-1.5 text-xs font-mono"
          >
            {[50, 100, 200, 500, 1000].map((n) => <option key={n} value={n}>last {n}</option>)}
          </select>
          <button
            onClick={runDiagnostics}
            disabled={diagBusy}
            data-testid="admin-logs-diagnostics"
            className="flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-60"
          >
            <Activity className={`w-3 h-3 ${diagBusy ? "animate-pulse" : ""}`} /> Run diagnostics
          </button>
          <button
            onClick={load}
            disabled={busy}
            data-testid="admin-logs-refresh"
            className="flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-60 rounded-md px-3 py-1.5 text-xs font-medium"
          >
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={runDeployCheck}
            data-testid="admin-logs-deploy-check"
            className="flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 rounded-md px-3 py-1.5 text-xs font-medium"
          >
            <CheckCircle2 className="w-3 h-3" /> Deploy check
          </button>
          <button
            onClick={shareLogUrl}
            data-testid="admin-logs-share-url"
            className="flex items-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-800 rounded-md px-3 py-1.5 text-xs font-medium"
          >
            <Zap className="w-3 h-3" /> Share URL
          </button>
          <button
            onClick={copyForE1}
            data-testid="admin-logs-copy"
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 text-xs font-medium"
          >
            <Copy className="w-3 h-3" /> Copy for E1
          </button>
          <button
            onClick={downloadBundle}
            data-testid="admin-logs-download"
            title="Saves the full log bundle as a .json file — bypasses the clipboard size cap (use this when 'Copy for E1' truncates at 500/1000)"
            className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-md px-3 py-1.5 text-xs font-medium"
          >
            <Download className="w-3 h-3" /> Download .json
          </button>
        </div>
      </div>

      {copyFallback && (
        <div className="mb-4 border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="admin-logs-fallback">
          <div className="text-xs font-mono text-amber-800 mb-2">
            Clipboard blocked by browser. Select all below (Ctrl/Cmd+A) and copy manually:
          </div>
          <textarea
            value={copyFallback}
            readOnly
            data-testid="admin-logs-fallback-textarea"
            onFocus={(e) => e.target.select()}
            className="w-full h-40 text-[10px] font-mono bg-white border border-amber-200 rounded p-2"
          />
        </div>
      )}

      {diagnostics && (
        <div className="mb-4 border border-slate-200 bg-white rounded-md p-4" data-testid="diagnostics-panel">
          <div className="flex items-center gap-2 mb-3">
            {diagnostics.ok
              ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              : <XCircle className="w-4 h-4 text-red-600" />}
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-600">
              Diagnostics · {diagnostics.ok ? "all green" : "failing"}
            </div>
            <div className="text-[10px] font-mono text-slate-400 ml-auto">
              {TS(diagnostics.generated_at)}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {diagnostics.checks?.map((c, i) => (
              <div
                key={`${c.check}-${i}`}
                data-testid={`diag-${c.check}`}
                className={`border rounded p-2 text-xs ${c.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono font-semibold text-slate-800">{c.check}</div>
                  <div className="font-mono text-[10px] text-slate-500">{c.duration_ms}ms</div>
                </div>
                {c.ok ? (
                  <pre className="mt-1 text-[10px] font-mono text-emerald-900 whitespace-pre-wrap break-all">
                    {JSON.stringify(c.info, null, 2)}
                  </pre>
                ) : (
                  <div className="mt-1 text-[10px] font-mono text-red-800 break-all">{c.error}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {deployCheck && (
        <div
          data-testid="deploy-check-panel"
          className={`mb-4 border rounded-md p-4 ${deployCheck.all_ok ? "border-emerald-200 bg-emerald-50" : "border-rose-300 bg-rose-50"}`}
        >
          <div className="flex items-center gap-2 mb-2">
            {deployCheck.all_ok
              ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              : <XCircle className="w-4 h-4 text-rose-600" />}
            <div className="text-sm font-semibold">
              Deploy check · marker <code className="px-1 rounded bg-white">{deployCheck.actual_marker}</code>
              {deployCheck.actual_marker !== deployCheck.expected_marker && (
                <span className="ml-2 text-rose-700">(expected {deployCheck.expected_marker})</span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs font-mono">
            {deployCheck.checks.map((c, i) => (
              <div key={`${c.check}-${i}`} className={`flex items-center gap-2 ${c.ok ? "text-emerald-800" : "text-rose-800"}`} data-testid={`deploy-check-row-${i}`}>
                {c.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                <span>{c.check}</span>
                {c.latency_ms !== undefined && <span className="text-slate-500 ml-auto">{c.latency_ms}ms</span>}
                {c.error && <span className="text-rose-700 truncate">{c.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───── HEALTH HISTORY — standalone admin-only callout ───── */}
      {healthHistory && (
        <section
          data-testid="health-history-panel"
          className="mb-5 rounded-xl border border-slate-900/10 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 shadow-sm overflow-hidden"
        >
          <header className="px-5 py-3 flex items-center gap-3 border-b border-slate-700/60">
            <div className="rounded-full bg-rose-500/20 p-1.5 ring-1 ring-rose-400/30">
              <Heart className="w-4 h-4 text-rose-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold tracking-tight">Health history</div>
              <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                Auto-sampled every 5 min · admin only · pruned after 30 days
              </div>
            </div>
            <select
              value={healthWindow}
              onChange={(e) => setHealthWindow(parseInt(e.target.value, 10))}
              data-testid="health-window-select"
              className="bg-slate-700 border border-slate-600 rounded-md px-2 py-1 text-[11px] font-mono text-slate-200"
            >
              {[1, 6, 12, 24, 72, 168, 720].map((h) => (
                <option key={h} value={h}>
                  {h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`}
                </option>
              ))}
            </select>
          </header>

          {healthHistory.count === 0 ? (
            <div className="px-5 py-6 text-xs font-mono text-slate-400" data-testid="health-history-empty">
              No snapshots captured yet — scheduler runs every 5 min. Check back shortly.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-5 py-4 border-b border-slate-700/40">
                <div>
                  <div className="text-[10px] font-mono uppercase text-slate-400">Uptime</div>
                  <div className={`text-2xl font-semibold mt-0.5 ${healthHistory.uptime_pct >= 99 ? "text-emerald-300" : healthHistory.uptime_pct >= 95 ? "text-amber-300" : "text-rose-300"}`}>
                    {healthHistory.uptime_pct !== null ? `${healthHistory.uptime_pct}%` : "—"}
                  </div>
                  <div className="text-[10px] font-mono text-slate-500">
                    {healthHistory.count} samples
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase text-slate-400">Health p-avg</div>
                  <div className={`text-2xl font-semibold mt-0.5 ${(healthHistory.avg_health_ms || 0) > 200 ? "text-amber-300" : "text-emerald-300"}`}>
                    {healthHistory.avg_health_ms ?? "—"}<span className="text-sm text-slate-400 ml-1">ms</span>
                  </div>
                  <div className="text-[10px] font-mono text-slate-500">
                    max {healthHistory.max_health_ms ?? "—"}ms
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase text-slate-400">Ready p-avg</div>
                  <div className={`text-2xl font-semibold mt-0.5 ${(healthHistory.avg_ready_ms || 0) > 400 ? "text-amber-300" : "text-emerald-300"}`}>
                    {healthHistory.avg_ready_ms ?? "—"}<span className="text-sm text-slate-400 ml-1">ms</span>
                  </div>
                  <div className="text-[10px] font-mono text-slate-500">
                    max {healthHistory.max_ready_ms ?? "—"}ms
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase text-slate-400">Deploys</div>
                  <div className="text-2xl font-semibold mt-0.5 text-indigo-300">
                    {healthHistory.transitions?.length ?? 0}
                  </div>
                  <div className="text-[10px] font-mono text-slate-500">
                    marker changes
                  </div>
                </div>
              </div>

              {healthHistory.snapshots?.length > 1 && (
                <div className="px-2 pt-2 pb-1" data-testid="health-history-chart">
                  <ResponsiveContainer width="100%" height={120}>
                    <LineChart data={healthHistory.snapshots} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                      <XAxis
                        dataKey="ts"
                        tickFormatter={(t) => t ? t.slice(11, 16) : ""}
                        stroke="#94a3b8"
                        tick={{ fontSize: 10, fontFamily: "monospace" }}
                        minTickGap={40}
                      />
                      <YAxis
                        stroke="#94a3b8"
                        tick={{ fontSize: 10, fontFamily: "monospace" }}
                        width={36}
                        label={{ value: "ms", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 10 }}
                      />
                      <Tooltip
                        contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 11, fontFamily: "monospace", color: "#e2e8f0" }}
                        labelFormatter={(t) => t ? new Date(t).toLocaleString() : ""}
                      />
                      {healthHistory.transitions?.map((tr) => (
                        <ReferenceLine
                          key={tr.ts || tr.marker}
                          x={tr.ts}
                          stroke="#818cf8"
                          strokeDasharray="3 3"
                          label={{ value: tr.marker?.split("_")[0] || "deploy", fill: "#a5b4fc", fontSize: 9, position: "top" }}
                        />
                      ))}
                      <Line
                        type="monotone"
                        dataKey="health_ms"
                        stroke="#34d399"
                        strokeWidth={1.5}
                        dot={false}
                        name="/health"
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="ready_ms"
                        stroke="#60a5fa"
                        strokeWidth={1.5}
                        dot={false}
                        name="/health/ready"
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {healthHistory.transitions?.length > 0 && (
                <div className="px-5 py-3 border-t border-slate-700/40">
                  <div className="text-[10px] font-mono uppercase text-slate-400 mb-1.5">
                    Marker transitions ({healthHistory.transitions.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid="health-transitions">
                    {healthHistory.transitions.slice(-6).map((tr) => (
                      <span
                        key={`${tr.ts}-${tr.marker}`}
                        className="inline-flex items-center gap-1.5 bg-indigo-500/10 border border-indigo-400/30 rounded-full px-2 py-0.5 text-[10px] font-mono text-indigo-200"
                      >
                        <span className="text-slate-400">{tr.ts ? tr.ts.slice(5, 16).replace("T", " ") : ""}</span>
                        <span className="font-semibold">{tr.marker}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {healthHistory.recent_failures?.length > 0 && (
                <div className="px-5 py-3 border-t border-rose-900/40 bg-rose-900/20">
                  <div className="text-[10px] font-mono uppercase text-rose-300 mb-1.5">
                    Recent failures ({healthHistory.recent_failures.length})
                  </div>
                  <div className="space-y-1" data-testid="health-failures">
                    {healthHistory.recent_failures.slice(-5).map((f) => (
                      <div key={f.ts} className="text-[11px] font-mono text-rose-100">
                        <span className="text-slate-400">{f.ts ? f.ts.slice(5, 19).replace("T", " ") : ""}</span>
                        <span className="ml-2">{(f.failed_checks || []).join(", ") || "marker mismatch"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {fbLatency && fbLatency.count > 0 && (
        <div className="mb-4 border border-slate-200 bg-white rounded-md p-4" data-testid="fb-latency-panel">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-amber-500" />
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-600">
              FB API latency · last {fbLatency.count} calls
            </div>
            <div className="flex items-center gap-3 ml-auto text-[10px] font-mono">
              <span className="text-slate-500">avg <b className={fbLatency.avg_ms > 3000 ? "text-red-600" : fbLatency.avg_ms > 1500 ? "text-amber-600" : "text-emerald-600"}>{fbLatency.avg_ms}ms</b></span>
              <span className="text-slate-500">p95 <b className={fbLatency.p95_ms > 8000 ? "text-red-600" : fbLatency.p95_ms > 4000 ? "text-amber-600" : "text-emerald-600"}>{fbLatency.p95_ms}ms</b></span>
              <span className="text-emerald-600">{fbLatency.ok} ok</span>
              {fbLatency.err > 0 && <span className="text-red-600">{fbLatency.err} err</span>}
            </div>
          </div>

          {fbLatency.per_account?.length > 0 && (
            <div className="mb-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="fb-latency-per-account">
              {fbLatency.per_account.slice(0, 6).map((a) => (
                <div
                  key={a.ad_account_id}
                  data-testid={`fb-latency-account-${a.ad_account_id}`}
                  className={`border rounded p-2 text-xs ${a.err > 0 ? "border-red-200 bg-red-50" : a.avg_ms > 4000 ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-mono font-semibold text-slate-800 truncate">{a.ad_account_id}</div>
                    <div className="font-mono text-[10px] text-slate-500">{a.calls}×</div>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-[10px] font-mono">
                    <span>avg <b>{a.avg_ms}ms</b></span>
                    <span>max <b>{a.max_ms}ms</b></span>
                    {a.err > 0 && <span className="text-red-700">{a.err} err</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => debugAdAccount(a.ad_account_id)}
                    disabled={acctDebugBusy}
                    data-testid={`fb-acct-debug-${a.ad_account_id}`}
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-mono text-blue-700 hover:text-blue-900 disabled:opacity-60"
                  >
                    <Search className="w-3 h-3" /> Debug this account
                  </button>
                </div>
              ))}
            </div>
          )}

          <details>
            <summary className="text-[10px] font-mono text-slate-500 cursor-pointer hover:text-slate-700">
              show all {fbLatency.entries?.length || 0} calls (newest first)
            </summary>
            <div className="mt-2 max-h-60 overflow-y-auto border border-slate-100 rounded">
              <table className="w-full text-[10px] font-mono">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-500">
                    <th className="px-2 py-1">ts</th>
                    <th className="px-2 py-1">context</th>
                    <th className="px-2 py-1">ad_account</th>
                    <th className="px-2 py-1 text-right">ms</th>
                    <th className="px-2 py-1">retries</th>
                    <th className="px-2 py-1">ok</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(fbLatency.entries || [])].reverse().map((e, i) => (
                    <tr key={`${e.ts}-${e.ad_account_id}-${i}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fb-latency-row-${i}`}>
                      <td className="px-2 py-1 text-slate-400">{(e.ts || "").slice(11, 19)}</td>
                      <td className="px-2 py-1 text-slate-700">{e.context}</td>
                      <td className="px-2 py-1 text-slate-700">{e.ad_account_id}</td>
                      <td className={`px-2 py-1 text-right font-semibold ${e.latency_ms > 4000 ? "text-red-700" : e.latency_ms > 2000 ? "text-amber-700" : "text-slate-800"}`}>{e.latency_ms}</td>
                      <td className="px-2 py-1 text-slate-500">{e.retries > 0 ? `${e.retries}×` : "—"}</td>
                      <td className="px-2 py-1">{e.ok ? <span className="text-emerald-600">ok</span> : <span className="text-red-700" title={e.note || ""}>err</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Client events panel */}
        <div className="border border-slate-200 bg-white rounded-md" data-testid="client-events-panel">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 text-[11px] font-mono uppercase tracking-wider text-slate-600">
            <Monitor className="w-3.5 h-3.5" /> Client events · {clientEvents.length}
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-100">
            {clientEvents.length === 0 && (
              <div className="p-4 text-xs text-slate-500 font-mono">
                No client events yet. They'll appear here when any browser running this app hits an error.
              </div>
            )}
            {clientEvents.map((ev, i) => (
              <RowBoundary key={`${ev.ts || ""}-${ev.url || ""}-${i}`} ev={ev} index={i}>
              <div className="p-3 hover:bg-slate-50 text-xs" data-testid={`client-event-${i}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-mono text-[10px] text-slate-500">{TS(ev.ts)}</span>
                  <span className="font-mono text-[10px] bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">{txt(ev.source)}</span>
                  <StatusPill status={ev.status} />
                  {ev.method && <span className="font-mono text-[10px] text-slate-600">{txt(ev.method)}</span>}
                  {ev.duration_ms !== null && ev.duration_ms !== undefined && (
                    <span className="font-mono text-[10px] text-slate-400">{txt(ev.duration_ms)}ms</span>
                  )}
                  {ev.email && <span className="font-mono text-[10px] text-blue-700">{txt(ev.email)}</span>}
                </div>
                <div className="font-mono text-[11px] text-slate-800 break-words">{txt(ev.message)}</div>
                {ev.url && <div className="font-mono text-[10px] text-slate-500 break-all mt-1">{txt(ev.url)}</div>}
                {ev.page && <div className="font-mono text-[10px] text-slate-400 mt-0.5">on {txt(ev.page)}</div>}
                {ev.response_body && (
                  <details className="mt-1">
                    <summary className="text-[10px] font-mono text-slate-500 cursor-pointer">response body</summary>
                    <pre className="mt-1 text-[10px] font-mono bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">{txt(ev.response_body)}</pre>
                  </details>
                )}
                {(ev.request_headers || ev.request_body) && (
                  <details className="mt-1" data-testid={`client-event-${i}-request`}>
                    <summary className="text-[10px] font-mono text-indigo-700 cursor-pointer">request envelope</summary>
                    <div className="mt-1 text-[10px] font-mono bg-indigo-50 border border-indigo-200 rounded p-2 max-h-60 overflow-y-auto space-y-2">
                      {ev.request_headers && (
                        <div>
                          <div className="text-indigo-700 font-semibold">headers</div>
                          <pre className="whitespace-pre-wrap break-all text-slate-800">{JSON.stringify(ev.request_headers, null, 2)}</pre>
                        </div>
                      )}
                      {ev.request_body && (
                        <div>
                          <div className="text-indigo-700 font-semibold">body</div>
                          <pre className="whitespace-pre-wrap break-all text-slate-800">{ev.request_body}</pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}
                {ev.response_headers && (
                  <details className="mt-1" data-testid={`client-event-${i}-response-headers`}>
                    <summary className="text-[10px] font-mono text-purple-700 cursor-pointer">response headers</summary>
                    <pre className="mt-1 text-[10px] font-mono bg-purple-50 border border-purple-200 rounded p-2 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">{JSON.stringify(ev.response_headers, null, 2)}</pre>
                  </details>
                )}
                {Array.isArray(ev.route_history) && ev.route_history.length > 0 && (
                  <details className="mt-1" data-testid={`client-event-${i}-routes`}>
                    <summary className="text-[10px] font-mono text-emerald-700 cursor-pointer">
                      route history · {ev.route_history.length}
                    </summary>
                    <div className="mt-1 text-[10px] font-mono bg-emerald-50 border border-emerald-200 rounded p-2 max-h-40 overflow-y-auto">
                      {ev.route_history.map((r, j) => (
                        <div key={j} className="break-all">
                          <span className="text-slate-400">{(typeof r.ts === "string" ? r.ts : "").slice(11, 19)}</span>
                          <span className="text-slate-800 ml-2">{txt(r.path)}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {ev.session_snapshot && (
                  <details className="mt-1" data-testid={`client-event-${i}-session`}>
                    <summary className="text-[10px] font-mono text-slate-600 cursor-pointer">session snapshot</summary>
                    <pre className="mt-1 text-[10px] font-mono bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap break-all">{JSON.stringify(ev.session_snapshot, null, 2)}</pre>
                  </details>
                )}
                {ev.stack && (
                  <details className="mt-1">
                    <summary className="text-[10px] font-mono text-slate-500 cursor-pointer">stack</summary>
                    <pre className="mt-1 text-[10px] font-mono bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap break-all max-h-60 overflow-y-auto">{txt(ev.stack)}</pre>
                  </details>
                )}
                {Array.isArray(ev.console_mirror) && ev.console_mirror.length > 0 && (
                  <details className="mt-1" data-testid={`client-event-${i}-console`}>
                    <summary className="text-[10px] font-mono text-amber-700 cursor-pointer">
                      console mirror · {ev.console_mirror.length} {ev.console_mirror.length === 1 ? "entry" : "entries"} (last 60s)
                    </summary>
                    <div className="mt-1 text-[10px] font-mono bg-amber-50 border border-amber-200 rounded p-2 max-h-60 overflow-y-auto space-y-1">
                      {ev.console_mirror.map((c, j) => (
                        <div key={j} className="break-all">
                          <span className={c.level === "error" ? "text-red-700" : "text-amber-700"}>
                            [{txt(c.level)}]
                          </span>
                          <span className="text-slate-400 mx-1">{(typeof c.ts === "string" ? c.ts : "").slice(11, 19)}</span>
                          <span className="text-slate-800">
                            {Array.isArray(c.args) ? c.args.map((x) => txt(x)).join(" ") : txt(c.args)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
              </RowBoundary>
            ))}
          </div>
        </div>

        {/* Backend logs panel */}
        <div className="border border-slate-200 bg-white rounded-md" data-testid="backend-logs-panel">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 text-[11px] font-mono uppercase tracking-wider text-slate-600">
            <Server className="w-3.5 h-3.5" /> Backend supervisor tail
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {Object.keys(backendLogs).length === 0 && (
              <div className="p-4 text-xs text-slate-500 font-mono">No backend log files found.</div>
            )}
            {Object.entries(backendLogs).map(([name, lines]) => (
              <div key={name} className="border-b border-slate-100 last:border-b-0">
                <div className="px-4 py-1.5 bg-slate-50 text-[10px] font-mono text-slate-600 sticky top-0 border-b border-slate-100">
                  {name} · {lines.length} lines
                </div>
                <pre className="px-4 py-2 text-[10px] font-mono text-slate-800 whitespace-pre-wrap break-words" data-testid={`backend-file-${name}`}>
                  {lines.length === 0 ? "(empty)" : lines.join("\n")}
                </pre>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FB ad account debug modal */}
      {acctDebug && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
          onClick={() => setAcctDebug(null)}
          data-testid="fb-acct-debug-modal"
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-blue-600" />
                <div className="font-semibold text-slate-900 font-mono text-sm">{acctDebug.ad_account_id}</div>
              </div>
              <button onClick={() => setAcctDebug(null)} className="p-1 rounded hover:bg-slate-100" data-testid="fb-acct-debug-close">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              {acctDebug.loading && (
                <div className="text-slate-500 font-mono text-xs flex items-center gap-2">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Calling Facebook…
                </div>
              )}
              {acctDebug.verdict && (
                <div
                  className={`rounded-lg p-4 border ${
                    acctDebug.verdict.severity === "critical" ? "bg-red-50 border-red-300" :
                    acctDebug.verdict.severity === "high" ? "bg-rose-50 border-rose-300" :
                    acctDebug.verdict.severity === "warn" ? "bg-amber-50 border-amber-300" :
                    "bg-emerald-50 border-emerald-300"
                  }`}
                  data-testid="fb-acct-debug-verdict"
                >
                  <div className={`text-xs font-mono uppercase tracking-wider mb-1 ${
                    acctDebug.verdict.severity === "critical" ? "text-red-700" :
                    acctDebug.verdict.severity === "high" ? "text-rose-700" :
                    acctDebug.verdict.severity === "warn" ? "text-amber-700" :
                    "text-emerald-700"
                  }`}>
                    Verdict · {acctDebug.verdict.severity}
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mb-2">{txt(acctDebug.verdict.summary)}</div>
                  <div className="text-xs text-slate-700 leading-relaxed">{txt(acctDebug.verdict.fix)}</div>
                  {acctDebug.verdict.detail && (
                    <div className="mt-2 text-[11px] font-mono text-slate-500 break-words">{txt(acctDebug.verdict.detail)}</div>
                  )}
                </div>
              )}
              {/* Token validity check */}
              {acctDebug.token_check && (
                <div className={`border rounded p-3 ${acctDebug.token_check.ok ? "border-slate-200" : "border-red-300 bg-red-50"}`}>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Token self-check · /me</div>
                  {acctDebug.token_check.ok ? (
                    <div className="text-xs text-slate-700">
                      Authenticated as <b>{txt(acctDebug.token_check.me_name)}</b>
                      <span className="font-mono text-slate-500 ml-2">{txt(acctDebug.token_check.me_id)}</span>
                      <span className="ml-2 text-[10px] font-mono text-slate-400">({acctDebug.token_check.ms}ms)</span>
                    </div>
                  ) : (
                    <div className="text-xs text-red-800 font-mono break-words">{txt(acctDebug.token_check.body || acctDebug.token_check.error)}</div>
                  )}
                </div>
              )}
              {/* Account visible on /me/adaccounts check */}
              {acctDebug.on_me_adaccounts && (
                <div className={`border rounded p-3 ${
                  acctDebug.on_me_adaccounts.found_in_list === false ? "border-red-300 bg-red-50" :
                  !acctDebug.on_me_adaccounts.ok ? "border-amber-300 bg-amber-50" : "border-slate-200"
                }`}>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Permission · /me/adaccounts</div>
                  {acctDebug.on_me_adaccounts.ok ? (
                    acctDebug.on_me_adaccounts.found_in_list ? (
                      <div className="text-xs text-slate-700">
                        <CheckCircle2 className="w-3 h-3 inline text-emerald-600" /> On the token's visible ad-account list
                        <span className="ml-2 text-[10px] text-slate-500">{acctDebug.on_me_adaccounts.total_accessible} accounts accessible total</span>
                      </div>
                    ) : (
                      <div className="text-xs text-red-800">
                        <XCircle className="w-3 h-3 inline" /> <b>NOT on the token's accessible ad-account list.</b>
                        <div className="mt-1 text-[11px]">Token can see {acctDebug.on_me_adaccounts.total_accessible} accounts but not this one — this is usually why Insights calls fail even when FB UI shows the account is fine.</div>
                      </div>
                    )
                  ) : (
                    <div className="text-xs text-amber-800 font-mono break-words">{txt(acctDebug.on_me_adaccounts.body || acctDebug.on_me_adaccounts.error)}</div>
                  )}
                </div>
              )}
              {acctDebug.ok === false && (
                <div className="border border-red-200 bg-red-50 rounded p-3 text-xs">
                  <div className="font-semibold text-red-800 mb-1">Facebook returned an error</div>
                  <pre className="text-[11px] text-red-900 whitespace-pre-wrap break-words">{txt(acctDebug.error)}</pre>
                  {acctDebug.api_error_code !== undefined && (
                    <div className="mt-2 text-[11px] text-red-700">code: <b>{txt(acctDebug.api_error_code)}</b></div>
                  )}
                  {acctDebug.api_error_subcode && (
                    <div className="text-[11px] text-red-700">subcode: <b>{txt(acctDebug.api_error_subcode)}</b></div>
                  )}
                  {acctDebug.http_status && (
                    <div className="text-[11px] text-red-700">http: <b>{txt(acctDebug.http_status)}</b></div>
                  )}
                </div>
              )}
              {acctDebug.account_meta && (
                <div className="border border-slate-200 rounded p-3">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">Account info</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    <div className="text-slate-500">name</div>
                    <div className="font-mono text-slate-900 truncate">{acctDebug.account_meta.name || "—"}</div>
                    <div className="text-slate-500">status</div>
                    <div className="font-mono">
                      <span className={`px-1.5 py-0.5 rounded ${acctDebug.account_status_label === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                        {acctDebug.account_status_label} ({acctDebug.account_meta.account_status})
                      </span>
                    </div>
                    {acctDebug.account_meta.disable_reason !== undefined && acctDebug.account_meta.disable_reason !== 0 && (
                      <>
                        <div className="text-slate-500">disable_reason</div>
                        <div className="font-mono text-red-700">{acctDebug.account_meta.disable_reason}</div>
                      </>
                    )}
                    <div className="text-slate-500">currency</div>
                    <div className="font-mono text-slate-900">{acctDebug.account_meta.currency} · {acctDebug.account_meta.country}</div>
                    <div className="text-slate-500">timezone</div>
                    <div className="font-mono text-slate-900 truncate">{acctDebug.account_meta.timezone}</div>
                    <div className="text-slate-500">amount_spent / spend_cap</div>
                    <div className="font-mono text-slate-900">
                      {acctDebug.account_meta.amount_spent || 0} / {acctDebug.account_meta.spend_cap || "—"}
                    </div>
                    <div className="text-slate-500">balance</div>
                    <div className="font-mono text-slate-900">{acctDebug.account_meta.balance || "—"}</div>
                    <div className="text-slate-500">prepay</div>
                    <div className="font-mono text-slate-900">{String(acctDebug.account_meta.is_prepay_account)}</div>
                  </div>
                </div>
              )}
              {acctDebug.campaigns && (
                <div className="border border-slate-200 rounded p-3">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">Campaigns</div>
                  <div className="text-xs">
                    Total <b>{acctDebug.campaigns.total}</b>{" "}
                    {Object.entries(acctDebug.campaigns.by_status || {}).map(([s, n]) => (
                      <span key={s} className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 font-mono text-slate-700">{s}: {n}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="border border-slate-200 rounded p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">Latency profile</div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-[10px] text-slate-500 font-mono">metadata</div>
                    <div className={`font-mono font-semibold ${acctDebug.meta_latency_ms > 4000 ? "text-red-700" : acctDebug.meta_latency_ms > 1500 ? "text-amber-700" : "text-emerald-700"}`}>{acctDebug.meta_latency_ms || 0}ms</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500 font-mono">campaigns list</div>
                    <div className={`font-mono font-semibold ${acctDebug.campaigns_latency_ms > 4000 ? "text-red-700" : acctDebug.campaigns_latency_ms > 1500 ? "text-amber-700" : "text-emerald-700"}`}>{acctDebug.campaigns_latency_ms || 0}ms</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500 font-mono">total</div>
                    <div className={`font-mono font-semibold ${acctDebug.total_latency_ms > 8000 ? "text-red-700" : acctDebug.total_latency_ms > 3000 ? "text-amber-700" : "text-emerald-700"}`}>{acctDebug.total_latency_ms || 0}ms</div>
                  </div>
                </div>
                {acctDebug.recent_latency && (
                  <div className="mt-2 text-[11px] font-mono text-slate-600">
                    Recent {acctDebug.recent_latency.calls} calls · avg <b>{acctDebug.recent_latency.avg_ms}ms</b> · max <b>{acctDebug.recent_latency.max_ms}ms</b>
                    {acctDebug.recent_latency.err > 0 && <span className="text-red-700"> · {acctDebug.recent_latency.err} err</span>}
                  </div>
                )}
              </div>
              {acctDebug.circuit && (
                <div className={`border rounded p-3 ${acctDebug.circuit.is_open ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">Circuit breaker</div>
                  {acctDebug.circuit.is_open ? (
                    <div className="text-xs text-amber-900">
                      Currently <b>OPEN</b>. Reopens in {Math.max(0, Math.floor((acctDebug.circuit.reopen_at_ts - Date.now() / 1000) / 60))}m.
                      {acctDebug.circuit.details?.last_reason && (
                        <pre className="mt-1 text-[10px] text-amber-800 whitespace-pre-wrap break-words">{acctDebug.circuit.details.last_reason}</pre>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-700">Closed · {acctDebug.circuit.details?.failures_in_window || 0} fails in window</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
